import { Game, type GameOverReason, type StartOptions } from './game';
import { api, playerId, riderName, setRiderName, type ConfigResponse } from './api';
import { RaceClient } from './net/race';
import { CAMPAIGN, THEMES, getTheme } from '../shared/themes';
import { UNITS_PER_METRE } from '../shared/constants';
import type { GameMode, GhostFrame, RunSummary, ScoreRow, ThemeId } from '../shared/types';
import type { Sim } from '../shared/sim';

const canvas = document.getElementById('stage') as HTMLCanvasElement;
const ui = document.getElementById('ui') as HTMLDivElement;

// --- local progress ---------------------------------------------------

interface Progress {
  cleared: ThemeId[];
  bestScore: Partial<Record<string, number>>;
  bestTime: Partial<Record<string, number>>;
  muted: boolean;
  seenHelp: boolean;
}

const PROGRESS_KEY = 'gnarwhal.progress.v1';

function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Progress>;
      return {
        cleared: Array.isArray(p.cleared) ? p.cleared : [],
        bestScore: p.bestScore ?? {},
        bestTime: p.bestTime ?? {},
        muted: Boolean(p.muted),
        seenHelp: Boolean(p.seenHelp),
      };
    }
  } catch {
    /* corrupt storage is not worth crashing over */
  }
  return { cleared: [], bestScore: {}, bestTime: {}, muted: false, seenHelp: false };
}

let progress = loadProgress();

function saveProgress(): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch {
    /* private mode */
  }
}

function isUnlocked(theme: ThemeId): boolean {
  const i = CAMPAIGN.indexOf(theme);
  if (i <= 0) return true;
  return progress.cleared.includes(CAMPAIGN[i - 1]!);
}

// --- app state --------------------------------------------------------

let config: ConfigResponse | null = null;
let race: RaceClient | null = null;
let lastRun: { summary: RunSummary; ghost: GhostFrame[]; sim: Sim } | null = null;

const game = new Game(canvas, {
  onEnd: (summary, ghost, sim, reason) => showResults(summary, ghost, sim, reason),
  onPause: (paused) => {
    if (!paused) hideScreen();
  },
});

// --- chrome -----------------------------------------------------------

function measureInsets(): void {
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const cs = getComputedStyle(probe);
  game.setInsets(Number.parseFloat(cs.paddingTop) || 0, Number.parseFloat(cs.paddingBottom) || 0);
  probe.remove();
}

function resize(): void {
  // Deliberately not setting an explicit height: #app is `position:fixed;inset:0`,
  // and a shorter explicit height lets the page background show under the canvas.
  game.resize();
}

window.addEventListener('resize', resize);
window.visualViewport?.addEventListener('resize', resize);
window.addEventListener('orientationchange', () => setTimeout(resize, 260));

// --- HUD controls -----------------------------------------------------

let pads: HTMLDivElement | null = null;
let sendPad: HTMLButtonElement | null = null;
let padTimer = 0;

function mountPads(): void {
  if (pads) return;
  pads = document.createElement('div');
  pads.className = 'pads';

  const send = document.createElement('button');
  send.className = 'pad pad--send is-cold';
  send.textContent = 'SEND';
  bindPad(send, (down) => game.input.setSend(down));

  const action = document.createElement('button');
  action.className = 'pad pad--action';
  action.innerHTML = 'HOP<br>TRICK';
  bindPad(action, (down) => {
    game.input.setTrick(down);
    if (down) {
      const sim = game.currentSim;
      if (!sim || sim.grounded) game.input.pressHop();
    }
  });

  pads.append(send, action);
  ui.append(pads);
  sendPad = send;

  const bar = document.createElement('div');
  bar.className = 'topbar';
  bar.dataset.role = 'topbar';
  const pause = document.createElement('button');
  pause.className = 'chip';
  pause.textContent = 'II';
  pause.onclick = () => {
    game.setPaused(true);
    showPause();
  };
  bar.append(pause);
  ui.append(bar);

  padTimer = window.setInterval(() => {
    const sim = game.currentSim;
    if (!sim || !sendPad) return;
    const ready = sim.flow >= 34 && !sim.sending;
    sendPad.classList.toggle('is-ready', ready);
    sendPad.classList.toggle('is-cold', !ready);
  }, 120);
}

function bindPad(el: HTMLElement, set: (down: boolean) => void): void {
  const down = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    set(true);
    navigator.vibrate?.(8);
    el.setPointerCapture?.(e.pointerId);
  };
  const up = (e: PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    set(false);
    el.releasePointerCapture?.(e.pointerId);
  };
  el.addEventListener('pointerdown', down);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);
  el.addEventListener('pointerleave', up);
  el.addEventListener('contextmenu', (e) => e.preventDefault());
}

function unmountPads(): void {
  pads?.remove();
  pads = null;
  sendPad = null;
  ui.querySelector('[data-role="topbar"]')?.remove();
  if (padTimer) {
    clearInterval(padTimer);
    padTimer = 0;
  }
}

// --- screen plumbing --------------------------------------------------

let screenEl: HTMLDivElement | null = null;

function showScreen(html: string, sheet = false): HTMLDivElement {
  hideScreen();
  const el = document.createElement('div');
  el.className = sheet ? 'screen screen--sheet' : 'screen';
  el.innerHTML = html;
  ui.append(el);
  screenEl = el;
  el.querySelectorAll('button').forEach((b) =>
    b.addEventListener('pointerdown', () => game.audio.sfx('menuMove')),
  );
  return el;
}

function hideScreen(): void {
  screenEl?.remove();
  screenEl = null;
}

function toast(text: string, ms = 2200): void {
  ui.querySelector('.toast')?.remove();
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = text;
  ui.append(t);
  setTimeout(() => t.remove(), ms);
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function fmtTime(ms: number): string {
  const total = Math.max(0, Math.round(ms / 100) / 10);
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return m > 0 ? `${m}:${s.toFixed(1).padStart(4, '0')}` : `${s.toFixed(1)}s`;
}

async function unlockAudio(): Promise<void> {
  await game.audio.unlock();
  game.audio.setMuted(progress.muted);
}

// --- title ------------------------------------------------------------

function showTitle(): void {
  game.stop();
  unmountPads();
  const daily = config?.daily;
  const el = showScreen(`
    <div class="brand">
      <h1>GNARWHAL</h1>
      <p>Downhill Dimensions</p>
    </div>
    <div class="menu">
      <button class="btn btn--primary" data-go="worlds">
        <span>RIDE THE CAMPAIGN<small>Six worlds, start to finish line</small></span>
        <kbd>▶</kbd>
      </button>
      <button class="btn" data-go="endless">
        <span>ENDLESS SURVIVAL<small>Three crashes. It never stops getting harder.</small></span>
        <kbd>∞</kbd>
      </button>
      <button class="btn" data-go="daily">
        <span>DAILY CHALLENGE<small>${daily ? `${esc(daily.name)} · ${esc(getTheme(daily.theme).name)}` : 'Same track for everyone, every day'}</small></span>
        <kbd>☀</kbd>
      </button>
      <button class="btn" data-go="race">
        <span>HEAD TO HEAD<small>Two phones, one trail, one code</small></span>
        <kbd>⚡</kbd>
      </button>
      <div class="row">
        <button class="btn btn--ghost" data-go="boards"><span>LEADERBOARDS</span></button>
        <button class="btn btn--ghost" data-go="settings"><span>SETTINGS</span></button>
      </div>
    </div>
    <div class="foot">build ${__BUILD__}${config ? ` · server v${esc(config.version)}` : ' · offline'} · tap anything to enable sound</div>
  `);

  el.addEventListener('click', async (e) => {
    const target = (e.target as HTMLElement).closest('[data-go]') as HTMLElement | null;
    if (!target) return;
    await unlockAudio();
    game.audio.sfx('menuSelect');
    switch (target.dataset.go) {
      case 'worlds':
        showWorlds();
        break;
      case 'endless':
        showEndlessPicker();
        break;
      case 'daily':
        startDaily();
        break;
      case 'race':
        showRaceEntry();
        break;
      case 'boards':
        showBoards(`trail:${CAMPAIGN[0]}`);
        break;
      case 'settings':
        showSettings();
        break;
    }
  });
}

// --- world select -----------------------------------------------------

function worldCard(theme: ThemeId, mode: 'trail' | 'endless'): string {
  const t = THEMES[theme];
  const locked = mode === 'trail' && !isUnlocked(theme);
  const bestT = progress.bestTime[`trail:${theme}`];
  const bestS = progress.bestScore[`${mode}:${theme}`];
  const stat =
    mode === 'trail'
      ? bestT
        ? `Best ${fmtTime(bestT)}`
        : 'Not yet ridden'
      : bestS
        ? `Best ${Math.round(bestS).toLocaleString()}`
        : 'No run yet';
  return `
    <button class="world ${locked ? 'is-locked' : ''}" data-theme="${theme}" style="background:linear-gradient(135deg, ${t.sky.mid}, ${t.sky.horizon} 55%, ${t.haze});">
      ${locked ? '<span class="lock">LOCKED</span>' : ''}
      <h3>${esc(t.name)}</h3>
      <p>${esc(t.tagline)}</p>
      <div class="stat">${esc(t.region)} · ${stat}</div>
    </button>`;
}

function showWorlds(): void {
  const el = showScreen(`
    <div class="title">Choose a trail</div>
    <p class="sub">Finish a world to open the next one. Trails have unlimited crashes — the clock and your score do the punishing.</p>
    <div class="worlds">${CAMPAIGN.map((t) => worldCard(t, 'trail')).join('')}</div>
    <div class="spacer"></div>
    <button class="btn btn--ghost" data-back><span>← Back</span></button>
  `);
  el.addEventListener('click', (e) => {
    const back = (e.target as HTMLElement).closest('[data-back]');
    if (back) return showTitle();
    const card = (e.target as HTMLElement).closest('[data-theme]') as HTMLElement | null;
    if (!card) return;
    const theme = card.dataset.theme as ThemeId;
    startRun({ mode: 'trail', theme, seed: `trail-${theme}` });
  });
}

function showEndlessPicker(): void {
  const el = showScreen(`
    <div class="title">Endless survival</div>
    <p class="sub">Three crashes and you are done. The trail keeps tightening forever, so every run ends — the only question is how far down.</p>
    <div class="worlds">${CAMPAIGN.map((t) => worldCard(t, 'endless')).join('')}</div>
    <div class="spacer"></div>
    <button class="btn btn--ghost" data-back><span>← Back</span></button>
  `);
  el.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-back]')) return showTitle();
    const card = (e.target as HTMLElement).closest('[data-theme]') as HTMLElement | null;
    if (!card) return;
    const theme = card.dataset.theme as ThemeId;
    startRun({ mode: 'endless', theme, seed: `endless-${theme}-${Date.now().toString(36)}`, length: 0 });
  });
}

function startDaily(): void {
  const d = config?.daily;
  if (!d) {
    toast('Daily challenge needs a connection');
    return;
  }
  startRun({
    mode: 'daily',
    theme: d.theme,
    seed: d.seed,
    length: THEMES[d.theme].length,
    difficultyMul: d.difficultyMul ?? 1,
  });
}

// --- running ----------------------------------------------------------

function startRun(o: Omit<StartOptions, 'seed'> & { seed: string }): void {
  hideScreen();
  mountPads();
  resize();
  game.start(o);
  if (!progress.seenHelp) {
    progress.seenHelp = true;
    saveProgress();
    toast('Left thumb steers. Flick up or tap HOP to jump.', 4200);
  }
}

function showPause(): void {
  const el = showScreen(
    `
    <div class="title">Paused</div>
    <div class="menu">
      <button class="btn btn--primary" data-act="resume"><span>RESUME</span></button>
      <button class="btn" data-act="restart"><span>RESTART RUN</span></button>
      <button class="btn" data-act="mute"><span>SOUND: ${progress.muted ? 'OFF' : 'ON'}</span></button>
      <button class="btn btn--ghost" data-act="quit"><span>END RUN</span></button>
    </div>
    <div class="spacer"></div>
    <div class="hint">
      <b>Stick</b><span>Rest your left thumb anywhere and slide to steer</span>
      <b>Flick ↑</b><span>Bunny hop. Hold the pad for extra height.</span>
      <b>Slide ↓</b><span>Tuck: faster on the ground, fast-fall in the air</span>
      <b>HOP/TRICK</b><span>Hop on the ground, trick in the air</span>
      <b>SEND</b><span>Burn flow for speed and double points</span>
    </div>
  `,
    true,
  );
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!act) return;
    switch (act.dataset.act) {
      case 'resume':
        hideScreen();
        game.setPaused(false);
        break;
      case 'restart': {
        const o = currentStart;
        if (o) {
          hideScreen();
          game.setPaused(false);
          game.start(o);
        }
        break;
      }
      case 'mute':
        progress.muted = !progress.muted;
        saveProgress();
        game.audio.setMuted(progress.muted);
        showPause();
        break;
      case 'quit':
        game.setPaused(false);
        game.stop('quit');
        break;
    }
  });
}

let currentStart: StartOptions | null = null;
const originalStart = game.start.bind(game);
game.start = (o: StartOptions) => {
  currentStart = o;
  originalStart(o);
};

// --- results ----------------------------------------------------------

async function showResults(
  summary: RunSummary,
  ghost: GhostFrame[],
  sim: Sim,
  reason: GameOverReason,
): Promise<void> {
  unmountPads();
  lastRun = { summary, ghost, sim };

  const boardKey = `${summary.mode}:${summary.theme}`;
  const prevScore = progress.bestScore[boardKey] ?? 0;
  const newScoreBest = summary.score > prevScore;
  if (newScoreBest) progress.bestScore[boardKey] = summary.score;
  if (summary.finished && summary.mode === 'trail') {
    const key = `trail:${summary.theme}`;
    const prevT = progress.bestTime[key];
    if (!prevT || summary.durationMs < prevT) progress.bestTime[key] = summary.durationMs;
    if (!progress.cleared.includes(summary.theme)) progress.cleared.push(summary.theme);
  }
  saveProgress();

  const t = getTheme(summary.theme);
  const nextIndex = CAMPAIGN.indexOf(summary.theme) + 1;
  const next = summary.mode === 'trail' && summary.finished ? CAMPAIGN[nextIndex] : undefined;

  const headline = summary.finished
    ? 'FINISH'
    : reason === 'dead'
      ? 'WIPED OUT'
      : 'RUN ENDED';

  const el = showScreen(`
    <div class="title">${headline} · ${esc(t.name)}</div>
    <div class="hero-score"><b>${Math.round(summary.score).toLocaleString()}</b><span>${newScoreBest ? 'New personal best' : 'Score'}</span></div>
    <div class="stats">
      <div class="stat-card"><b>${summary.distance.toLocaleString()}</b><span>metres</span></div>
      <div class="stat-card"><b>${fmtTime(summary.durationMs)}</b><span>time</span></div>
      <div class="stat-card"><b>${Math.round((summary.topSpeed / UNITS_PER_METRE) * 3.6)}</b><span>top km/h</span></div>
      <div class="stat-card"><b>${summary.tricks}</b><span>tricks</span></div>
      <div class="stat-card"><b>x${(1 + summary.bestCombo * 0.25).toFixed(2)}</b><span>best combo</span></div>
      <div class="stat-card"><b>${summary.crashes}</b><span>crashes</span></div>
    </div>
    <div class="narrative is-loading" data-role="narrative">Rolling the highlight reel…</div>
    <div class="menu">
      ${next ? `<button class="btn btn--primary" data-act="next"><span>NEXT WORLD<small>${esc(THEMES[next].name)} is open</small></span><kbd>▶</kbd></button>` : ''}
      <button class="btn ${next ? '' : 'btn--primary'}" data-act="retry"><span>RIDE IT AGAIN</span><kbd>↺</kbd></button>
      <button class="btn" data-act="board"><span>LEADERBOARD</span></button>
      <button class="btn btn--ghost" data-act="menu"><span>MAIN MENU</span></button>
    </div>
    <div class="foot" data-role="rank"></div>
  `);

  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!act) return;
    game.audio.sfx('menuSelect');
    switch (act.dataset.act) {
      case 'next':
        if (next) startRun({ mode: 'trail', theme: next, seed: `trail-${next}` });
        break;
      case 'retry':
        if (currentStart) startRun({ ...currentStart, seed: currentStart.seed });
        break;
      case 'board':
        showBoards(boardKeyFor(summary));
        break;
      case 'menu':
        showTitle();
        break;
    }
  });

  void submitAndNarrate(summary, ghost, sim, el);
}

function boardKeyFor(s: RunSummary): string {
  if (s.mode === 'daily') {
    const m = /^daily-(\d{4}-\d{2}-\d{2})$/.exec(s.seed);
    return `daily:${m ? m[1] : new Date().toISOString().slice(0, 10)}`;
  }
  return `${s.mode}:${s.theme}`;
}

async function submitAndNarrate(
  summary: RunSummary,
  ghost: GhostFrame[],
  sim: Sim,
  el: HTMLElement,
): Promise<void> {
  const narrative = el.querySelector('[data-role="narrative"]') as HTMLElement | null;
  const rank = el.querySelector('[data-role="rank"]') as HTMLElement | null;
  let name = riderName();
  if (!name) {
    name = (prompt('Rider name for the leaderboard (max 14 characters)') ?? '').trim();
    if (name) setRiderName(name);
  }
  try {
    const res = await api.submit({
      name: name || 'RIDER',
      playerId: playerId(),
      run: summary,
      ghost: ghost.length > 1 ? ghost : undefined,
      highlights: sim.highlights.slice(0, 60),
    });
    if (rank) {
      rank.textContent = res.best
        ? `#1 on ${res.board.replace(':', ' · ')} — top of the board`
        : `#${res.rank} on ${res.board.replace(':', ' · ')}`;
    }
    const n = await api.narrate(res.id);
    if (narrative) {
      narrative.classList.remove('is-loading');
      narrative.textContent = n.text;
    }
  } catch (err) {
    if (narrative) {
      narrative.classList.remove('is-loading');
      narrative.textContent = offlineNarrative(summary);
    }
    if (rank) rank.textContent = 'Offline — score saved on this device only';
    void err;
  }
}

function offlineNarrative(s: RunSummary): string {
  const t = getTheme(s.theme);
  if (s.crashes === 0 && s.finished) return `Clean through ${t.name}. Not a single dab.`;
  if (s.crashes >= 3) return `${t.name} took its toll: ${s.crashes} crashes and still ${s.distance} metres of it.`;
  return `${s.distance} metres of ${t.name}, ${s.tricks} tricks, and a top of ${Math.round((s.topSpeed / UNITS_PER_METRE) * 3.6)} km/h.`;
}

// --- leaderboards -----------------------------------------------------

const BOARD_TABS: { id: string; label: string }[] = [
  ...CAMPAIGN.map((t) => ({ id: `trail:${t}`, label: THEMES[t].name })),
  ...CAMPAIGN.map((t) => ({ id: `endless:${t}`, label: `∞ ${THEMES[t].name}` })),
];

async function showBoards(board: string): Promise<void> {
  const dailyId = config ? `daily:${config.daily.date}` : null;
  const tabs = [...BOARD_TABS, ...(dailyId ? [{ id: dailyId, label: 'Daily' }] : [])];
  const order: 'score' | 'time' = board.startsWith('trail:') ? 'time' : 'score';

  const el = showScreen(`
    <div class="title">Leaderboards</div>
    <div class="tabs">${tabs
      .map((t) => `<button class="tab ${t.id === board ? 'is-on' : ''}" data-board="${t.id}">${esc(t.label)}</button>`)
      .join('')}</div>
    <div class="board" data-role="rows"><p class="sub">Loading…</p></div>
    <div class="spacer"></div>
    <button class="btn btn--ghost" data-back><span>← Back</span></button>
  `);

  el.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-back]')) return showTitle();
    const tab = (e.target as HTMLElement).closest('[data-board]') as HTMLElement | null;
    if (tab) void showBoards(tab.dataset.board!);
  });

  const rows = el.querySelector('[data-role="rows"]') as HTMLElement;
  try {
    const res = await api.scores(board, { limit: 25, order });
    rows.innerHTML = res.rows.length
      ? res.rows.map((r, i) => boardRow(r, i + 1, order)).join('')
      : '<p class="sub">Nobody has ridden this one yet. Be first.</p>';
  } catch {
    rows.innerHTML = '<p class="sub">Could not reach the server. Scores are still saved locally.</p>';
  }
}

function boardRow(r: ScoreRow, rank: number, order: 'score' | 'time'): string {
  const mine = r.name === riderName();
  const primary = order === 'time' ? fmtTime(r.durationMs) : Math.round(r.score).toLocaleString();
  return `
    <div class="board-row ${mine ? 'is-me' : ''}">
      <span class="rank">${rank}</span>
      <div>
        <div>${esc(r.name)}</div>
        <div class="meta">${r.distance.toLocaleString()} m · ${order === 'time' ? `${Math.round(r.score).toLocaleString()} pts` : fmtTime(r.durationMs)}${r.highlight ? ' · ' + esc(r.highlight.slice(0, 60)) : ''}</div>
      </div>
      <b>${primary}</b>
    </div>`;
}

// --- settings ---------------------------------------------------------

function showSettings(): void {
  const el = showScreen(`
    <div class="title">Settings</div>
    <p class="sub">Rider name shows on the leaderboards.</p>
    <input type="text" maxlength="14" value="${esc(riderName())}" data-role="name" placeholder="RIDER" />
    <div class="spacer"></div>
    <div class="menu">
      <button class="btn" data-act="mute"><span>SOUND<small>${progress.muted ? 'Muted' : 'On'}</small></span><kbd>${progress.muted ? '✕' : '♪'}</kbd></button>
      <button class="btn" data-act="reset"><span>RESET PROGRESS<small>Clears unlocks and local bests</small></span></button>
      <button class="btn btn--ghost" data-act="back"><span>← Back</span></button>
    </div>
    <div class="hint">
      <b>Stick</b><span>Rest your left thumb anywhere and slide to steer</span>
      <b>Flick ↑</b><span>Bunny hop</span>
      <b>Slide ↓</b><span>Tuck</span>
      <b>HOP/TRICK</b><span>Hop on the ground, trick in the air</span>
      <b>SEND</b><span>Burn flow for speed and double points</span>
    </div>
  `);
  const input = el.querySelector('[data-role="name"]') as HTMLInputElement;
  input.addEventListener('change', () => setRiderName(input.value.trim().toUpperCase()));
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!act) return;
    if (act.dataset.act === 'mute') {
      progress.muted = !progress.muted;
      saveProgress();
      game.audio.setMuted(progress.muted);
      showSettings();
    } else if (act.dataset.act === 'reset') {
      if (confirm('Reset all local progress?')) {
        progress = { cleared: [], bestScore: {}, bestTime: {}, muted: progress.muted, seenHelp: true };
        saveProgress();
        toast('Progress reset');
        showTitle();
      }
    } else {
      setRiderName(input.value.trim().toUpperCase());
      showTitle();
    }
  });
}

// --- race -------------------------------------------------------------

function showRaceEntry(): void {
  const el = showScreen(`
    <div class="title">Head to head</div>
    <p class="sub">Both phones need the internet, not the same Wi-Fi. One of you starts a race and reads out the code.</p>
    <div class="menu">
      <button class="btn btn--primary" data-act="create"><span>START A RACE<small>Get a code to share</small></span><kbd>⚡</kbd></button>
      <input type="text" maxlength="4" placeholder="CODE" data-role="code" style="text-align:center" />
      <button class="btn" data-act="join"><span>JOIN WITH CODE</span></button>
      <button class="btn btn--ghost" data-act="back"><span>← Back</span></button>
    </div>
  `);
  const input = el.querySelector('[data-role="code"]') as HTMLInputElement;
  el.addEventListener('click', async (e) => {
    const act = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!act) return;
    if (act.dataset.act === 'back') return showTitle();
    if (act.dataset.act === 'create') {
      try {
        const { code } = await api.newRace();
        joinRace(code);
      } catch {
        toast('Could not reach the server');
      }
    } else if (act.dataset.act === 'join') {
      const code = input.value.trim().toUpperCase();
      if (code.length !== 4) return toast('Codes are four characters');
      joinRace(code);
    }
  });
}

function joinRace(code: string): void {
  race?.close();
  const name = riderName() || 'RIDER';
  const client = new RaceClient(code, name, (ev) => {
    if (ev.t === 'welcome' || ev.t === 'state') renderLobby(client);
    if (ev.t === 'go') startRace(client, ev.startsAt);
    if (ev.t === 'error') toast(ev.message);
    if (ev.t === 'closed') toast('Race disconnected');
  });
  race = client;
  client.connect();
  renderLobby(client);
}

function renderLobby(client: RaceClient): void {
  if (game.isRunning) return;
  const s = client.state;
  const el = showScreen(`
    <div class="title">Race lobby</div>
    <div class="code">${esc(client.code)}</div>
    <p class="sub">${s ? `${esc(getTheme(s.theme).name)} · ${Math.round((s.distance * 200) / UNITS_PER_METRE)} m` : 'Connecting…'}</p>
    <div class="riders">
      ${(s?.players ?? [])
        .map(
          (p) =>
            `<div class="rider-chip"><span>${esc(p.name)}${p.id === client.you ? ' (you)' : ''}</span><span class="ok">${p.ready ? 'READY' : p.connected ? 'WAITING' : 'GONE'}</span></div>`,
        )
        .join('') || '<div class="rider-chip"><span>Waiting for riders…</span></div>'}
    </div>
    <div class="spacer"></div>
    <div class="menu">
      <button class="btn btn--primary" data-act="ready"><span>I'M READY<small>Starts when everyone is</small></span></button>
      <button class="btn btn--ghost" data-act="leave"><span>← Leave</span></button>
    </div>
  `);
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null;
    if (!act) return;
    if (act.dataset.act === 'ready') {
      client.ready(true);
      toast('Ready — waiting for the others');
    } else {
      client.close();
      race = null;
      showTitle();
    }
  });
}

function startRace(client: RaceClient, startsAt: number): void {
  const s = client.state;
  if (!s) return;
  hideScreen();
  mountPads();
  resize();
  game.start({
    mode: 'race',
    theme: s.theme,
    seed: s.seed,
    length: s.distance,
    startAt: startsAt,
    now: () => client.serverNow(),
    rival: () => {
      const st = client.state;
      if (!st) return null;
      const other = st.players.find((p) => p.id !== client.you && p.connected);
      return other ? { z: other.z, x: other.x, y: other.y, name: other.name } : null;
    },
    onTick: (sim) => {
      client.pushState({
        z: sim.z,
        x: sim.x,
        y: sim.y,
        speed: sim.speed,
        score: sim.score,
        crashes: sim.crashes,
      });
      if (sim.finished) client.finish(sim.timeMs, sim.score);
    },
  });
}

// --- boot -------------------------------------------------------------

document.addEventListener('gesturestart', (e) => e.preventDefault());
document.addEventListener('dblclick', (e) => e.preventDefault());
// iOS suspends the audio context on backgrounding and never restarts it on its
// own. Any tap is a valid gesture to get it back, so treat every tap as one.
document.addEventListener('pointerdown', () => game.audio.resume(), { capture: true, passive: true });
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    if (game.isRunning && !game.isPaused) {
      game.setPaused(true);
      showPause();
    }
  } else {
    void game.audio.unlock();
  }
});

measureInsets();
resize();
showTitle();

api
  .config()
  .then((c) => {
    config = c;
    if (!screenEl?.querySelector('.brand')) return;
    showTitle();
  })
  .catch(() => {
    /* offline: the game still runs, just without boards */
  });

if ('serviceWorker' in navigator && location.protocol === 'https:') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* not fatal */
    });
  });
}

void (game as unknown as { fps: number }).fps;
// Handy from the browser console when something looks wrong mid-run.
(window as unknown as Record<string, unknown>).__gnarwhal = { game, progress: () => progress };
export {};
